import { ThemeProvider } from '@servicetitan/design-system';
import { ComponentProps } from 'react';
import { ColorPaletteFamilyShades } from './color-palette-catalog';

/*
 * Derived from ThemeProvider's own prop type rather than importing `@servicetitan/anvil-themes`
 * directly (a transitive dependency only) — avoids adding an undeclared direct dependency.
 */
type Anvil1Theme = NonNullable<ComponentProps<typeof ThemeProvider>['theme']>;

/*
 * This portal's Button/SideNav/Card components come from `@servicetitan/design-system`
 * (Anvil1), which reads a *different* CSS-custom-property namespace (`--colorsX`, set via
 * Anvil1's own `ThemeProvider`/`theme` shape from `@servicetitan/anvil-themes`) than Anvil2's
 * `ThemeProvider` (`--kebab-case` names from `@servicetitan/hammer-token`'s `Token` schema).
 * Overriding Anvil2 tokens alone would not re-theme any component actually rendered by this
 * portal app. This module builds the Anvil1 `theme` shape instead.
 */

export type { ColorPaletteFamilyShades };

/**
 * Builds an Anvil1 `theme` override object from a resolved palette family's shades.
 *
 * Token mapping (confirmed against installed `@servicetitan/design-system@14.5.1` compiled CSS):
 * - `colors.surface.primary` / `colors.text.onPrimary` -> `--colorsSurfacePrimary` /
 *   `--colorsTextOnPrimary` -> primary-fill Button background/foreground (REQ-SET-PALETTE-003).
 * - `colors.text.primary` -> `--colorsTextPrimary` -> themed text/links across the WHOLE app
 *   (main content pages' "View" links, etc), not just the nav — `ColorPaletteThemeProvider` mounts
 *   this theme once at the app-router level, so this token is global. Kept as `shades.primary`
 *   so those links stay theme-colored (REQ-SET-PALETTE-004 AC1's non-nav scope). The nav
 *   selected-tab text specifically needs a fixed dark neutral for WCAG AA against `secondary`
 *   (see `portal-layout-menu.module.less`'s `.menu-links` override) rather than solving it here,
 *   since fixing it here regressed every other themed link app-wide (22 of 23 catalog families
 *   failed WCAG AA for `shades.primary` on `shades.secondary`, but that pairing is nav-local).
 * - `colors.hover.primary` -> `--colorsHoverPrimary` -> nav selected-tab highlight background
 *   (REQ-SET-PALETTE-004 AC1). Kept as `shades.secondary` (dark text on secondary passes WCAG AA
 *   for every catalog family). NOT `shades.lightest`: the surrounding nav panel itself is tinted
 *   with that same `lightest` value (REQ-SET-PALETTE-005, --colorsSurfacePrimarySubdued), so a
 *   `lightest`-on-`lightest` highlight would be invisible against its own panel — confirmed
 *   visually where "Proposals" showed bold text with no visible highlight box at all.
 * - `colors.border.primary` / `colors.focus.ringPrimary` -> outline/focus-ring color, kept in
 *   sync with primary so focus rings don't visually clash with a re-themed button.
 * - `colors.surface.primarySubdued` -> `--colorsSurfacePrimarySubdued` -> structural panel tint
 *   (REQ-SET-PALETTE-005 AC1), using the family's lightest shade.
 */
export const buildColorPaletteTheme = (shades: ColorPaletteFamilyShades): Anvil1Theme => ({
    colors: {
        surface: {
            primary: shades.primary,
            primarySubdued: shades.lightest,
        },
        text: {
            primary: shades.primary,
            onPrimary: '#ffffff',
        },
        hover: {
            primary: shades.secondary,
        },
        border: {
            primary: shades.primary,
        },
        focus: {
            ringPrimary: shades.primary,
        },
    },
});
